import { InformationCircleIcon, WrenchIcon } from "@heroicons/react/24/outline";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import CollapsibleContainer from "@via/components/collapsible-container/collapsible-container.tsx";
import { GenericError } from "@via/components/error.tsx";
import FileInputButton from "@via/components/inputs/file-input-button.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component";
import FormTextareaComponent from "@via/components/inputs/form-textarea-component";
import ValueInputButton from "@via/components/inputs/value-input-button.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import { HeadingSize, PageHeading } from "@via/components/page-heading.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import Table, { TableSize } from "@via/components/table/table.tsx";
import {
  useDeepCompareCallback,
  useDeepCompareEffect,
} from "@via/hooks/useDeepCompare.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import HorizontalInputsArray from "@via/pages/projects/runs/overview/inputs/horizontal-inputs-array/horizontal-inputs-array.tsx";
import InputFileModal from "@via/pages/projects/runs/overview/inputs/input-file/input-file-modal.tsx";
import InputSingleFileModal from "@via/pages/projects/runs/overview/inputs/input-file/input-single-file-modal.tsx";
import InputSpreadsheet from "@via/pages/projects/runs/overview/inputs/input-spreadsheet/input-spreadsheet.tsx";
import InputValueModal from "@via/pages/projects/runs/overview/inputs/input-value/input-value-modal.tsx";
import ShowSettingsModal from "@via/pages/projects/runs/overview/inputs/show-settings-modal/show-settings-modal.tsx";
import IntegrationTokenSelector from "@via/pages/projects/runs/overview/inputs/integration-token-selector.tsx";
import { getVmetaCollectionLink } from "@via/pages/projects/runs/overview/utils/vmeta-dataset-menu-items";
import {
  addProfileVar,
  applyAutoFillConditions,
  applyAutoFillProcessConditions,
  cleanUpAutofillConditions,
  convertAndCapitalizeString,
  decodeGenericCond,
  filterDuplicatesByProperty,
  filterOutShowSett,
  getDataInputs,
  getPipeInputs,
  getProcessAndModuleOptions,
  getProcessParamsInputs,
  groupProcessInputs,
  parseAutofill,
  parseAutofillProcesses,
  parseDropdownOptions,
  parseEditorProcesses,
  parseSingleFileEditors,
  resolveRequiredInputGroups,
  shouldDisplayInput,
  getNestedValue,
  type AutoFill,
  type InputNode,
  type ProcessAndModuleOptions,
} from "@via/pages/projects/runs/utils/helpers.tsx";
import { ERROR } from "@via/utils/constants.tsx";
import {
  classNames,
  isObjectEmpty,
  getDirtyFieldPaths,
} from "@via/utils/helpers.tsx";
import { createMarkup } from "@via/utils/html-utils.tsx";
import { type CollectionItem } from "@via/utils/interfaces.tsx";
import {
  InputType,
  type GetRunDetailsResponseBody,
  type GetRunPipelineScriptsResponseBody,
  type SaveRunInputs,
  type RunParameterListResponseBody,
  type ViaPipelineRunInput,
} from "common";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type JSX,
} from "react";
import { type UseFormReturn } from "react-hook-form";
import { Tooltip } from "react-tooltip";

const sort: Array<{ id: string; desc: boolean }> = [
  {
    id: "name",
    desc: false,
  },
];

interface ViaPipelineInputsProps {
  form: UseFormReturn;
  runPipelineScripts: GetRunPipelineScriptsResponseBody;
  paramsInputs: SaveRunInputs[];
  setParamsInputs: Dispatch<SetStateAction<SaveRunInputs[]>>;
  setRequiredGlobalInputs: Dispatch<SetStateAction<InputNode[]>>;
  setDataInputs: Dispatch<SetStateAction<InputNode[]>>;
  setRequiredInputGroups: Dispatch<SetStateAction<InputNode[][]>>;
  pipelineRunDetails: RunParameterListResponseBody;
  runSettingsData: GetRunDetailsResponseBody;
  isRunSettingsSuccess: boolean;
  isRunSettingsLoading: boolean;
  isRunSettingsError: boolean;
  isSaveRunSuccess: boolean;
  readOnly: boolean;
}

const ViaPipelineInputs: FC<ViaPipelineInputsProps> = ({
  form,
  runPipelineScripts,
  paramsInputs,
  setParamsInputs,
  setRequiredGlobalInputs,
  setDataInputs,
  setRequiredInputGroups,
  pipelineRunDetails,
  runSettingsData,
  isRunSettingsSuccess,
  isRunSettingsLoading,
  isRunSettingsError,
  isSaveRunSuccess,
  readOnly,
}) => {
  const isInitialRender = useRef(true);
  const { dirtyFields } = form.formState;
  const lastDirtyFieldPathRef = useRef<string | null>(null);

  const setLastDirtyFieldPath = (path: string) => {
    lastDirtyFieldPathRef.current = path;
  };

  const { setNotification, NOTIFICATION } = useNotifications();

  const { control, setValue, getValues, watch } = form;

  const [allGlobalInputs, setAllGlobalInputs] = useState<InputNode[]>([]);
  const [allPipelineInputs, setAllPipelineInputs] = useState<InputNode[]>([]);
  const [allProcessAndModuleInputs, setAllProcessAndModuleInputs] = useState<
    ProcessAndModuleOptions[]
  >([]);

  const [inputNodes, setInputNodes] = useState<InputNode[]>([]);
  const [pipelineOptions, setPipelineOptions] = useState<InputNode[]>([]);
  const [processAndModuleOptions, setProcessAndModuleOptions] = useState<
    ProcessAndModuleOptions[]
  >([]);
  const [openInputFileModal, setOpenInputFileModal] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [openInputSingleFileModal, setOpenInputSingleFileModal] =
    useState<Record<string, unknown> | null>(null);
  const [singleFileEditor, setSingleFileEditor] = useState<
    Record<string, any> | undefined
  >();
  const [openInputValueModal, setOpenInputValueModal] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [openShowSettingsModal, setOpenShowSettingsModal] = useState<string[]>(
    []
  );
  const [systemInputs, setSystemInputs] = useState<string[]>([]);
  const [autoFill, setAutoFill] = useState<AutoFill[]>([]);
  const [autoFillProcesses, setAutoFillProcesses] = useState<
    Record<string, AutoFill[]>
  >({});
  const [editorProcesses, setEditorProcesses] = useState<Record<string, any>>(
    {}
  );

  const isRunInputsLoading = isRunSettingsLoading;
  const isRunInputsError = isRunSettingsError;

  const watchedFormValues = watch();
  const prevFormValuesRef = useRef({});

  useEffect(() => {
    if (isRunInputsLoading) {
      isInitialRender.current = true;
    }
  }, [isRunInputsLoading]);

  const checkAutoFillConditions = useDeepCompareCallback(
    (prevFormValues: Record<string, any>) => {
      const hasAutoFillApplied = applyAutoFillConditions(
        autoFill,
        watchedFormValues,
        prevFormValues,
        setValue,
        lastDirtyFieldPathRef.current
      );
      if (hasAutoFillApplied) {
        setNotification({
          id: "autofill",
          message: "AutoFill Processes Applied!",
          variant: NOTIFICATION.SUCCESS,
        });
      }
    },
    [watchedFormValues, autoFill, setValue]
  );

  const checkAutoFillProcessConditions = useDeepCompareCallback(
    (prevFormValues: Record<string, any>) => {
      const hasAutoFillApplied = applyAutoFillProcessConditions(
        autoFillProcesses,
        watchedFormValues,
        prevFormValues,
        setValue,
        lastDirtyFieldPathRef.current
      );
      if (hasAutoFillApplied) {
        setNotification({
          id: "autofill",
          message: "AutoFill Processes Applied!",
          variant: NOTIFICATION.SUCCESS,
        });
      }
    },
    [watchedFormValues, autoFillProcesses, setValue]
  );

  // Initialize prevFormValuesRef when data is loaded and form is ready
  // This runs separately to ensure proper initialization after form values are set
  useEffect(() => {
    if (!isRunInputsLoading && isInitialRender.current) {
      // Use a microtask to ensure this runs after all setValue calls from form initialization
      queueMicrotask(() => {
        if (isInitialRender.current) {
          isInitialRender.current = false;
          prevFormValuesRef.current = JSON.stringify(getValues());
        }
      });
    }
  }, [isRunInputsLoading, getValues]);

  useDeepCompareEffect(() => {
    // Only process autofill after initial render is complete
    if (!isRunInputsLoading && !isInitialRender.current) {
      const dirtyFieldPaths = getDirtyFieldPaths(dirtyFields);

      // Skip autofill on mount and runs only on genuine user-triggered input changes.
      if (dirtyFieldPaths.size > 0) {
        const prevFormValues = !isObjectEmpty(prevFormValuesRef.current)
          ? JSON.parse(`${prevFormValuesRef.current}`)
          : {};

        // Find last dirty field by comparing values
        for (const path of dirtyFieldPaths) {
          const prevVal = getNestedValue(prevFormValues, path);
          const currVal = getNestedValue(watchedFormValues, path);
          if (prevVal !== currVal) {
            setLastDirtyFieldPath(path);
            break;
          }
        }

        if (autoFill.length > 0) {
          checkAutoFillConditions(prevFormValues);
        }
        if (!isObjectEmpty(autoFillProcesses)) {
          checkAutoFillProcessConditions(prevFormValues);
        }

        // Intentionally update only when dirtyFields exist
        // This prevents storing transient or programmatic value changes as "previous" state
        prevFormValuesRef.current = JSON.stringify(watchedFormValues);
      }
    }
  }, [
    dirtyFields,
    watchedFormValues,
    autoFill,
    autoFillProcesses,
    checkAutoFillConditions,
    checkAutoFillProcessConditions,
  ]);

  useEffect(() => {
    if (isRunSettingsSuccess) {
      const { mainScriptHeaderConfig, processScriptHeaders } =
        runPipelineScripts;

      const { autoFill, systemInputs } = parseAutofill(mainScriptHeaderConfig);
      const autoFillProcesses = parseAutofillProcesses(processScriptHeaders);
      setEditorProcesses(parseEditorProcesses(processScriptHeaders));

      const profileVariables = runSettingsData.runEnvironment.availableList.map(
        ({ id, variable, hostname }) => ({ id, variable, hostname })
      );
      let autoFillArray = addProfileVar(autoFill, profileVariables);
      autoFillArray = decodeGenericCond(autoFillArray, profileVariables);
      autoFillArray = cleanUpAutofillConditions(autoFillArray);
      setAutoFill(autoFillArray);
      setSystemInputs(systemInputs);
      setAutoFillProcesses(autoFillProcesses);

      const { pipeParamsArray, pipeNonParamsArray } = getPipeInputs(
        pipelineRunDetails.inputs.pipe.schema
      );

      const processParamsInputs = filterDuplicatesByProperty(
        getProcessParamsInputs(pipelineRunDetails.inputs.processes),
        "name"
      );

      const inputsCircle = getPipeInputs(
        pipelineRunDetails.inputs.pipe.inputNodes ?? []
      );

      const showSettArray = filterDuplicatesByProperty(
        [
          ...pipeParamsArray,
          ...pipeNonParamsArray,
          ...inputsCircle.pipeParamsArray,
        ],
        "name"
      ).filter(item => item.inputDetails?.showSett);

      // globalInputs contains all inputs which start with 'params.' from the pipe.schema, processes (non pipe) and inputsCircle (main_pipeline_)
      let globalInputs = filterDuplicatesByProperty(
        [
          ...pipeParamsArray,
          ...processParamsInputs,
          ...inputsCircle.pipeParamsArray,
        ],
        "name"
      );

      const singleFileEditors = parseSingleFileEditors(mainScriptHeaderConfig);

      globalInputs = globalInputs.map(item =>
        // Check if the globalInputs's `id` exists in the singleFileEditors keys
        // Update inputDetails.editor with the corresponding singleFileEditors
        singleFileEditors[item.id] && item.qualifier === "singleFile"
          ? {
              ...item,
              inputDetails: {
                ...item.inputDetails,
                editor: singleFileEditors[item.id],
              },
            }
          : item
      );

      setRequiredGlobalInputs(
        globalInputs.filter(({ inputDetails }) => !inputDetails?.optional)
      );

      // Kept separate from the required set on purpose: a pipeline with more
      // than one entry point has to mark every one of them @optional, so these
      // never gate the Run button and are only used to warn.
      setDataInputs(getDataInputs(globalInputs));

      // @required_one_of groups, resolved against the same inputs. A group
      // naming an input that is not here is dropped, never enforced (#443).
      setRequiredInputGroups(
        resolveRequiredInputGroups(
          pipelineRunDetails.inputs.pipe.style,
          globalInputs
        )
      );

      const pipelineInputs = filterDuplicatesByProperty(
        pipeNonParamsArray,
        "name"
      );
      const processAndModuleInputs = getProcessAndModuleOptions(
        pipelineRunDetails.inputs.processes
      );

      // allGlobalInputs and allPipelineInputs are required for the show settings modal
      setAllGlobalInputs(globalInputs);
      setAllPipelineInputs(pipelineInputs);
      // allProcessAndModuleInputs are required for the show settings modal, pipelineOptions and processAndModuleOptions
      setAllProcessAndModuleInputs(processAndModuleInputs);

      // The main inputs are obtained by isolating the showSettingsArray inputs from the global inputs
      const globalInputsWithoutShowSett = filterOutShowSett(
        globalInputs,
        showSettArray
      );

      // Pipeline options are obtained by isolating the showSettingsArray inputs from the pipeline inputs
      const pipelineInputsWithoutShowSett = filterOutShowSett(
        pipelineInputs,
        showSettArray
      );

      setInputNodes(globalInputsWithoutShowSett);
      setPipelineOptions(pipelineInputsWithoutShowSett);
      setProcessAndModuleOptions(
        processAndModuleInputs.filter(
          ({ longVarName, processName, processOrgName }) =>
            !showSettArray.some(
              s =>
                s?.inputDetails?.showSett?.split(",")?.includes(longVarName) ||
                s?.inputDetails?.showSett
                  ?.split(",")
                  ?.includes(processOrgName) ||
                s?.inputDetails?.showSett?.split(",")?.includes(processName)
            )
        )
      );

      const { inputs, processOptions } = runSettingsData;

      // Set system input default values
      systemInputs.forEach(key => {
        setValue(key, undefined);
      });

      // Set main input default values
      globalInputs
        .filter(({ inputDetails }) => inputDetails?.defVal)
        .forEach(({ id, inputDetails }) => {
          setValue(
            id,
            inputDetails.checkbox
              ? inputDetails.defVal === "true" || inputDetails.defVal === true
              : inputDetails.defVal
          );
        });

      const pipeArrayInputsDefaultValues = groupProcessInputs(
        pipelineInputsWithoutShowSett,
        pipelineRunDetails?.inputs?.pipe?.style ?? []
      ).filter(({ isArray }) => isArray);

      const pipeSpreadSheetInputsDefaultValues = groupProcessInputs(
        pipelineInputsWithoutShowSett,
        pipelineRunDetails?.inputs?.pipe?.style ?? []
      ).filter(({ isSpreadsheet }) => isSpreadsheet);

      // Set pipeline input default values
      pipelineInputs.forEach(({ id, inputDetails }) => {
        // Default values for array inputs
        if (
          pipelineRunDetails?.inputs?.pipe?.style?.[0]?.array?.length > 0 &&
          pipeArrayInputsDefaultValues.some(({ inputGroup }) =>
            inputGroup.some(input => input.id === id)
          )
        ) {
          // Multiple rows of default values
          if (
            inputDetails?.defVal !== undefined &&
            Array.isArray(inputDetails.defVal)
          ) {
            inputDetails.defVal.forEach((val, index) => {
              setValue(`${id}_ind${index + 1}`, val);
            });
          }
        }
        // Default values for spreadsheet inputs
        else if (
          pipelineRunDetails?.inputs?.pipe?.style?.[0]?.spreadsheet?.length >
            0 &&
          pipeSpreadSheetInputsDefaultValues.some(({ inputGroup }) =>
            inputGroup.some(input => input.id === id)
          )
        ) {
          setValue(
            id,
            Array.isArray(inputDetails.defVal)
              ? inputDetails.defVal
              : [inputDetails.defVal === undefined ? "" : inputDetails.defVal]
          );
        }
        // Default values for non-array and non-spreadsheet inputs
        else {
          setValue(
            id,
            inputDetails.checkbox
              ? inputDetails.defVal === "true" || inputDetails.defVal === true
              : inputDetails.defVal === undefined
                ? ""
                : inputDetails.defVal
          );
        }
      });

      const arrayInputsDefaultValues = processAndModuleInputs.flatMap(
        ({ inputs, style }) =>
          groupProcessInputs(inputs, style).filter(({ isArray }) => isArray)
      );

      const spreadSheetInputsDefaultValues = processAndModuleInputs.flatMap(
        ({ inputs, style }) =>
          groupProcessInputs(inputs, style).filter(
            ({ isSpreadsheet }) => isSpreadsheet
          )
      );

      // Set process and module input default values
      processAndModuleInputs.forEach(({ inputs, style }) => {
        inputs.forEach(({ id, inputDetails }) => {
          // Default values for array inputs
          if (
            style?.[0]?.array?.length > 0 &&
            arrayInputsDefaultValues.some(({ inputGroup }) =>
              inputGroup.some(input => input.id === id)
            )
          ) {
            // Multiple rows of default values
            if (
              inputDetails?.defVal !== undefined &&
              Array.isArray(inputDetails.defVal)
            ) {
              inputDetails.defVal.forEach((val, index) => {
                setValue(`${id}_ind${index + 1}`, val);
              });
            }
          }
          // Default values for spreadsheet inputs
          else if (
            style?.[0]?.spreadsheet?.length > 0 &&
            spreadSheetInputsDefaultValues.some(({ inputGroup }) =>
              inputGroup.some(input => input.id === id)
            )
          ) {
            setValue(
              id,
              Array.isArray(inputDetails.defVal)
                ? inputDetails.defVal
                : [inputDetails.defVal === undefined ? "" : inputDetails.defVal]
            );
          }
          // Default values for non-array and non-spreadsheet inputs
          else {
            setValue(
              id,
              inputDetails.checkbox
                ? inputDetails.defVal === "true" || inputDetails.defVal === true
                : inputDetails.defVal === undefined
                  ? ""
                  : inputDetails.defVal
            );
          }
        });
      });

      // Reset global input values if inputs are present before
      if ((inputs as ViaPipelineRunInput[])?.length > 0) {
        const inputNames = (inputs as ViaPipelineRunInput[])?.map(
          ({ name }) => name
        );
        const currentValues = getValues("params") || {};
        Object.keys(currentValues)
          .filter(key => !inputNames.includes(key))
          .forEach(key => {
            setValue(`params.${key}`, undefined);
          });
      }
      // Set global input values
      (inputs as ViaPipelineRunInput[])?.forEach(({ name, value }) => {
        setValue(`params.${name}`, value);
      });

      // Set pipe and process/module input values
      if (processOptions) {
        Object.entries(processOptions)
          .filter(([_, value]) => Object.keys(value as object).length > 0)
          .forEach(([parentKey, value]) => {
            Object.entries(value as object).forEach(
              ([childKey, childValue]) => {
                const flatKey = `${parentKey}.${childKey}`;
                setValue(flatKey, childValue);
              }
            );
          });
      }
    }
  }, [
    pipelineRunDetails,
    runPipelineScripts,
    runSettingsData,
    isRunSettingsSuccess,
    setValue,
  ]);

  const syncIntegrationTokenToParamsInputs = useCallback(
    (inputName: string, tokenId: string | null) => {
      setParamsInputs(prev => {
        const idx = prev.findIndex(p => p.name === inputName);
        const existing = idx >= 0 ? prev[idx] : null;
        const serviceTokenId = tokenId ? Number(tokenId) : undefined;
        const newEntry: SaveRunInputs = {
          name: inputName,
          type: InputType.Input,
          value: serviceTokenId != null ? String(serviceTokenId) : tokenId,
          ...(serviceTokenId != null && { serviceTokenId }),
          ...(existing &&
            "id" in existing &&
            existing.id != null && { id: existing.id }),
        };
        if (idx >= 0) {
          return prev.slice(0, idx).concat([newEntry], prev.slice(idx + 1));
        }
        if (tokenId) return prev.concat([newEntry]);
        return prev;
      });
    },
    [setParamsInputs]
  );

  // Sync integration token form values -> paramsInputs (source of truth for save).
  // Uses functional update only; paramsInputs not in deps to avoid loop.
  useEffect(() => {
    const integrationTokenNames = inputNodes
      .filter(n => n.inputDetails?.integrationToken)
      .map(n => n.name);
    if (integrationTokenNames.length === 0) return;
    const params = watchedFormValues?.params as
      | Record<string, unknown>
      | undefined;
    if (!params) return;

    setParamsInputs(prev => {
      let next = prev;
      for (const name of integrationTokenNames) {
        const formVal = params[name];
        const tokenId =
          formVal != null && formVal !== "" ? String(formVal) : null;
        const serviceTokenId = tokenId ? Number(tokenId) : undefined;
        const idx = next.findIndex(p => p.name === name);
        const cur = idx >= 0 ? next[idx] : null;
        const curValue = cur && "value" in cur ? cur.value : undefined;
        const curServiceTokenId =
          cur && "serviceTokenId" in cur
            ? (cur as { serviceTokenId?: number }).serviceTokenId
            : undefined;
        if (curValue === tokenId && curServiceTokenId === serviceTokenId)
          continue;
        const newEntry: SaveRunInputs = {
          name,
          type: InputType.Input,
          value: serviceTokenId != null ? String(serviceTokenId) : tokenId,
          ...(serviceTokenId != null && { serviceTokenId }),
          ...(cur && "id" in cur && cur.id != null && { id: cur.id }),
        };
        if (idx >= 0) {
          next = next.slice(0, idx).concat([newEntry], next.slice(idx + 1));
        } else if (tokenId) {
          next = next.concat([newEntry]);
        }
      }
      return next;
    });
  }, [watchedFormValues, inputNodes, setParamsInputs]);

  const renderIntegrationTokenComponent = (info): JSX.Element => {
    const tokenTypes = info.getValue()?.integrationToken?.type || [];
    const inputName = info.row.original.id.replace("params.", "");
    return (
      <div className="mr-3 w-80">
        <IntegrationTokenSelector
          control={control}
          name={info.row.original.id}
          tokenTypes={tokenTypes}
          readOnly={readOnly}
          runOwnerId={runSettingsData?.owner?.id}
          onSelectionChange={tokenId =>
            syncIntegrationTokenToParamsInputs(inputName, tokenId)
          }
        />
      </div>
    );
  };

  const renderSelectComponent = (info): JSX.Element => {
    const value = watch(info.row.original.id);
    return (
      <div className="flex w-full">
        <div className="mr-3 w-80">
          <SelectComponent
            control={control}
            name={info.row.original.id}
            options={
              info
                .getValue()
                ?.dropDown?.split(",")
                .map(option => ({ label: option, value: option })) ?? []
            }
            readOnly={readOnly}
          />
        </div>
        {info.getValue()?.showSett && value === "yes" && (
          <button
            className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={() => {
              setOpenShowSettingsModal(info.getValue().showSett.split(","));
            }}>
            <WrenchIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        )}
      </div>
    );
  };

  const renderInputComponent = (info): JSX.Element => (
    <FormInputComponent
      control={control}
      name={info.row.original.id}
      disabled={readOnly}
      className="w-80 -mt-2"
    />
  );

  const renderTextareaComponent = (info): JSX.Element => (
    <FormTextareaComponent
      control={control}
      name={info.row.original.id}
      disabled={readOnly}
      className="w-80 -mt-2"
    />
  );

  const renderCheckboxComponent = (info): JSX.Element => (
    <FormInputComponent
      control={control}
      name={info.row.original.id}
      type="checkbox"
      disabled={readOnly}
      className="h-6 w-6 -mt-2"
    />
  );

  const renderValueButton = (info): JSX.Element => (
    <ValueInputButton
      control={control}
      name={info.row.original.id}
      onOpenModal={value =>
        setOpenInputValueModal({ [info.row.original.id]: value })
      }
      onDeleteItem={() => setValue(info.row.original.id, null)}
      disabled={readOnly}
    />
  );

  const renderFileButton = (info): JSX.Element => {
    // Check if this input is a collection type by looking at paramsInputs
    // Only show collection icon if qualifier is not "singleFile" and input has collection data
    const inputName = info.row.original.id.replace("params.", "");
    const isCollectionInput =
      info.row.original.qualifier !== "singleFile" &&
      paramsInputs.some(
        param =>
          param.name === inputName &&
          ((param.type === InputType.Collection && param.collectionId) ||
            (param.type === InputType.VmetaCollection &&
              param.vmetaCollectionId))
      );

    const valueLinkTo = getVmetaCollectionLink(
      paramsInputs,
      info.row.original.id
    );

    return (
      <FileInputButton
        control={control}
        name={info.row.original.id}
        onOpenModal={value => {
          if (info.row.original.qualifier === "singleFile") {
            setOpenInputSingleFileModal({
              [info.row.original.id]: value,
            });
            setSingleFileEditor(info.row.original?.inputDetails?.editor);
          } else {
            setOpenInputFileModal({
              [info.row.original.id]: value,
            });
          }
        }}
        onDeleteFile={() => {
          if (info.row.original.qualifier === "singleFile") {
            handleDeleteSingleInputFile(info.row.original.id);
          } else {
            handleDeleteInputFiles(info.row.original.id);
          }
        }}
        disabled={readOnly}
        isCollection={isCollectionInput}
        valueLinkTo={valueLinkTo}
      />
    );
  };

  const renderInputDescription = (info): JSX.Element => (
    <div
      className="mt-1 max-w-[90vw] whitespace-normal text-xs italic text-gray-500"
      dangerouslySetInnerHTML={createMarkup(info)}
    />
  );

  const columnHelper = createColumnHelper<InputNode>();
  const columns = useMemo(() => {
    return [
      columnHelper.accessor("name", {
        cell: info => (
          <div className="flex flex-row items-center align-top">
            {info.row.original.inputDetails?.inLabelOpt ?? info.getValue()}{" "}
            {info.row.original.inputDetails?.optional && "(Optional)"}
            {info.row.original.inputDetails?.tool && (
              <InformationCircleIcon
                data-tooltip-id={`info-${info.row.original.id}`}
                data-tooltip-content={info.row.original.inputDetails.tool}
                className="mx-2 h-4"
              />
            )}
            <Tooltip
              id={`info-${info.row.original.id}`}
              className="z-50 max-w-xs whitespace-pre-line break-words"
            />
          </div>
        ),
        header: "Name",
      }),
      columnHelper.accessor("inputDetails", {
        cell: info => (
          <div>
            {info.getValue()?.integrationToken
              ? renderIntegrationTokenComponent(info)
              : info.getValue()?.dropDown
                ? renderSelectComponent(info)
                : info.getValue()?.checkbox
                  ? renderCheckboxComponent(info)
                  : info.getValue()?.textbox
                    ? renderTextareaComponent(info)
                    : info.row.original.qualifier === "val"
                      ? info.row.original.id.startsWith("params.")
                        ? renderValueButton(info)
                        : renderInputComponent(info)
                      : renderFileButton(info)}
            {info.getValue()?.inDescOpt &&
              renderInputDescription(info.getValue()?.inDescOpt)}
          </div>
        ),
        header: "Input",
        size: 375,
        minSize: 150,
      }),
    ] as Array<ColumnDef<InputNode>>;
  }, [readOnly, paramsInputs]);

  const handleSaveInputFiles = (
    inputId: string,
    collection: CollectionItem
  ): void => {
    if (inputId.startsWith("params.")) {
      const name = inputId.replace("params.", "");

      setParamsInputs((prev: SaveRunInputs[]) => {
        const idx = prev.findIndex(p => p.name === name);

        // Build the correct union member
        const next: SaveRunInputs = {
          name,
          value: collection.value ?? collection.collectionName,
          ...(collection.collectionType === InputType.VmetaCollection
            ? {
                vmetaCollectionId: collection.collectionId || null,
                type: InputType.VmetaCollection,
              }
            : {
                collectionId: Number(collection.collectionId) || null,
                type: InputType.Collection,
              }),
        };

        // Preserve `id` if updating an existing item
        if (idx !== -1) {
          const keepId = prev[idx]?.id;
          const replaced = keepId != null ? { ...next, id: keepId } : next;
          return prev.map((p, i) => (i === idx ? replaced : p));
        }

        // Append new
        return [...prev, next];
      });
    }

    setValue(inputId, collection.collectionName);
    setOpenInputFileModal(null);
  };

  const handleSaveSingleInputFile = (
    inputId: string,
    value: string | null
  ): void => {
    if (inputId.startsWith("params.")) {
      setParamsInputs(prevParamsInputs => {
        const itemName = inputId.replace("params.", "");
        const existingItem = prevParamsInputs.some(
          ({ name }) => name === itemName
        );

        if (existingItem) {
          return prevParamsInputs.map(item =>
            item.name === itemName
              ? {
                  ...item,
                  value,
                  collectionId: null,
                  vmetaCollectionId: null,
                  type: InputType.Input,
                }
              : item
          );
        } else {
          return [
            ...prevParamsInputs,
            {
              name: itemName,
              value,
              collectionId: null,
              vmetaCollectionId: null,
              type: InputType.Input,
            },
          ];
        }
      });
    }
    setValue(inputId, value);
    // Also the multiple file dialog's onSaveValue, which still closes from here. The
    // single file dialog is closed by Modal once its confirm reports success.
    setOpenInputFileModal(null);
  };

  const handleSaveInputValue = (
    inputId: string,
    value: string | null
  ): void => {
    setValue(inputId, value);
  };

  const handleDeleteInputFiles = (inputId: string): void => {
    if (inputId.startsWith("params.")) {
      setParamsInputs((prevParamsInputs: SaveRunInputs[]) =>
        prevParamsInputs.map(item =>
          item.name === inputId.replace("params.", "")
            ? {
                ...item,
                value: null,
                collectionId: null,
                vmetaCollectionId: null,
              }
            : item
        )
      );
    }
    setValue(inputId, null);
  };

  const handleDeleteSingleInputFile = (inputId: string): void => {
    setValue(inputId, null);
  };

  const renderInputOptions = (
    input: InputNode,
    index: number,
    style?: Record<string, any>[]
  ): JSX.Element => {
    const value = watch(input.id);

    // Check if the current form value represents a collection by looking at paramsInputs
    // Only show collection icon if qualifier is not "singleFile" and input has collection data
    const isCollectionValue =
      input.qualifier !== "singleFile" &&
      paramsInputs.some(
        param =>
          param.value === value &&
          ((param.type === InputType.Collection && param.collectionId) ||
            (param.type === InputType.VmetaCollection &&
              param.vmetaCollectionId))
      );
    const valueLinkTo = getVmetaCollectionLink(paramsInputs, input.id);

    return (
      <div
        key={index}
        className={classNames(
          "mb-5",
          shouldDisplayInput(
            input.id,
            input.inputId,
            style?.[0]?.condi || [],
            runPipelineScripts.mainScriptHeaderConfig,
            form
          )
            ? ""
            : "hidden"
        )}>
        <label className="flex flex-row items-center text-sm font-medium leading-6 text-gray-900">
          {input.inputDetails?.inLabelOpt ?? input.name}{" "}
          {input.inputDetails?.optional && "(Optional)"}
          {input.inputDetails?.tool && (
            <InformationCircleIcon
              data-tooltip-id={`info-${input.id}`}
              data-tooltip-content={input.inputDetails?.tool}
              className="mx-2 h-4"
            />
          )}
        </label>
        <Tooltip
          id={`info-${input.id}`}
          className="z-50 max-w-xs whitespace-pre-line break-words"
        />
        <div className="mt-2">
          {input.inputDetails?.integrationToken ? (
            <IntegrationTokenSelector
              control={control}
              name={input.id}
              tokenTypes={input.inputDetails.integrationToken.type}
              readOnly={readOnly}
              runOwnerId={runSettingsData?.owner?.id}
              onSelectionChange={tokenId =>
                syncIntegrationTokenToParamsInputs(input.name, tokenId)
              }
            />
          ) : input.inputDetails?.dropDown ? (
            <div className="flex w-full">
              <div className="w-full">
                <SelectComponent
                  control={control}
                  name={input.id}
                  options={parseDropdownOptions(
                    input.id,
                    input.inputDetails?.dropDown || "",
                    runPipelineScripts.mainScriptHeaderConfig,
                    form
                  )}
                  readOnly={readOnly}
                />
              </div>
              {input.inputDetails?.showSett && value === "yes" && (
                <button
                  className="ml-3 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={() => {
                    setOpenShowSettingsModal(
                      input.inputDetails?.showSett?.split(",") ?? []
                    );
                  }}>
                  <WrenchIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              )}
            </div>
          ) : input.inputDetails?.checkbox ? (
            <label className="align-items flex">
              <input
                // {...register(input.id)}
                type="checkbox"
                className="mr-2 block h-6 w-6 rounded-md border-0 text-indigo-600 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                checked={value === true || value === "true"}
                onChange={e => setValue(input.id, e.target.checked)}
                disabled={readOnly}
              />
              <div className="block text-sm font-medium leading-6 text-gray-900">
                {input.inputDetails?.inLabelOpt ?? input.name}
              </div>
            </label>
          ) : input.inputDetails?.textbox ? (
            <FormTextareaComponent
              control={control}
              name={input.id}
              disabled={readOnly}
            />
          ) : input.qualifier === "val" ? (
            <FormInputComponent
              control={control}
              name={input.id}
              disabled={readOnly}
            />
          ) : (
            <FileInputButton
              control={control}
              name={input.id}
              onOpenModal={value => {
                if (input.qualifier === "singleFile") {
                  setOpenInputSingleFileModal({ [input.id]: value });
                  setSingleFileEditor(input?.inputDetails?.editor);
                } else {
                  setOpenInputFileModal({ [input.id]: value });
                }
              }}
              onDeleteFile={() =>
                input.qualifier === "singleFile"
                  ? handleDeleteSingleInputFile(input.id)
                  : handleDeleteInputFiles(input.id)
              }
              disabled={readOnly}
              isCollection={isCollectionValue}
              valueLinkTo={valueLinkTo}
            />
          )}
        </div>
        {input.inputDetails?.inDescOpt &&
          renderInputDescription(input.inputDetails?.inDescOpt)}
      </div>
    );
  };

  return (
    <>
      <div className="mb-4 px-4 py-2 shadow-md ring-1 ring-gray-900/5 sm:mx-0 rounded-lg md:px-6 lg:px-6 md:py-3 lg:py-3 lg:col-span-2 lg:row-span-2 lg:row-end-2">
        <div className="flex items-center justify-between">
          <PageHeading title="Inputs" size={HeadingSize.Small} />
        </div>

        {isRunInputsLoading ? (
          <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
            <SkeletonLoader count={12} />
          </div>
        ) : isRunInputsError ? (
          <div className="via-generic-error">
            <GenericError message={ERROR} />
          </div>
        ) : (
          <>
            <Table
              key={`mainInputs-${isSaveRunSuccess ? "reset" : "normal"}-${runSettingsData?.runUUID}`}
              data={inputNodes.filter(({ id }) => !systemInputs.includes(id))}
              sort={sort}
              columns={columns}
              showPagination={false}
              pageSize={
                inputNodes.filter(({ id }) => !systemInputs.includes(id)).length
              }
              size={TableSize.Small}
              applyColumnSize={true}
            />
            {/* System Inputs Section */}
            {inputNodes.filter(({ id }) => systemInputs.includes(id)).length >
              0 && (
              <CollapsibleContainer
                title="System Inputs"
                key={`systemInputs-${runSettingsData?.runUUID}`}
                size={HeadingSize.XSmall}>
                <Table
                  key={`systemInputs-${isSaveRunSuccess ? "reset" : "normal"}-${runSettingsData?.runUUID}`}
                  data={inputNodes.filter(({ id }) =>
                    systemInputs.includes(id)
                  )}
                  sort={sort}
                  columns={columns}
                  showPagination={false}
                  pageSize={
                    inputNodes.filter(({ id }) => systemInputs.includes(id))
                      .length
                  }
                  size={TableSize.Small}
                  applyColumnSize={true}
                />
              </CollapsibleContainer>
            )}
          </>
        )}
        {!!openInputFileModal && (
          <InputFileModal
            fileOrCollection={openInputFileModal}
            setFileOrCollection={setOpenInputFileModal}
            onSaveFiles={handleSaveInputFiles}
            onSaveValue={handleSaveSingleInputFile}
            paramsInputs={paramsInputs}
            form={form}
          />
        )}
        {!!openInputSingleFileModal && (
          <InputSingleFileModal
            value={openInputSingleFileModal}
            setValue={setOpenInputSingleFileModal}
            onSave={handleSaveSingleInputFile}
            form={form}
            editor={singleFileEditor}
            runUUID={runSettingsData?.runUUID}
            paramsInputs={paramsInputs}
          />
        )}
        {!!openInputValueModal && (
          <InputValueModal
            value={openInputValueModal}
            setValue={setOpenInputValueModal}
            onSave={handleSaveInputValue}
          />
        )}
      </div>
      {pipelineOptions.length > 0 && (
        <CollapsibleContainer
          title="Pipeline Options"
          key={`pipelineOptions-${runSettingsData?.runUUID}`}>
          {groupProcessInputs(
            pipelineOptions,
            pipelineRunDetails?.inputs?.pipe?.style ?? []
          ).map(({ inputGroup, isArray, isSpreadsheet }, groupIndex) => (
            <div key={groupIndex}>
              {inputGroup.length === 1 ? (
                <>
                  {inputGroup[0].inputDetails?.title && (
                    <PageHeading
                      title={inputGroup[0].inputDetails.title}
                      size={HeadingSize.XSmall}
                    />
                  )}
                  {renderInputOptions(
                    inputGroup[0],
                    groupIndex,
                    pipelineRunDetails?.inputs?.pipe?.style ?? []
                  )}
                </>
              ) : (
                <div key={`multicol-group-${groupIndex}`}>
                  {isSpreadsheet ? (
                    <InputSpreadsheet
                      form={form}
                      inputs={inputGroup}
                      editorProcesses={editorProcesses}
                      paramsInputs={paramsInputs}
                    />
                  ) : (
                    <HorizontalInputsArray
                      form={form}
                      processOptions={runSettingsData?.processOptions}
                      inputs={inputGroup}
                      style={pipelineRunDetails?.inputs?.pipe?.style ?? []}
                      mainScriptHeaderConfig={
                        runPipelineScripts.mainScriptHeaderConfig
                      }
                      renderInputOptions={renderInputOptions}
                      isArray={isArray}
                      readOnly={readOnly}
                    />
                  )}
                </div>
              )}
            </div>
          ))}
        </CollapsibleContainer>
      )}
      {processAndModuleOptions.map(
        (
          { longVarName, processName, onlyModuleName, inputs, style },
          index
        ) => (
          <CollapsibleContainer
            key={`processAndModuleOptions-${runSettingsData?.runUUID}-${index}`}
            title={`${onlyModuleName ? `${convertAndCapitalizeString(onlyModuleName)}: ${convertAndCapitalizeString(processName)}` : convertAndCapitalizeString(longVarName)} Options`}>
            {groupProcessInputs(inputs, style).map(
              ({ inputGroup, isArray, isSpreadsheet }, groupIndex) => (
                <div key={groupIndex}>
                  {inputGroup.length === 1 ? (
                    <>
                      {inputGroup[0].inputDetails?.title && (
                        <PageHeading
                          title={inputGroup[0].inputDetails.title}
                          size={HeadingSize.XSmall}
                        />
                      )}
                      {renderInputOptions(inputGroup[0], groupIndex, style)}
                    </>
                  ) : (
                    <div key={`multicol-group-${groupIndex}`}>
                      {isSpreadsheet ? (
                        <InputSpreadsheet
                          form={form}
                          inputs={inputGroup}
                          editorProcesses={editorProcesses}
                          paramsInputs={paramsInputs}
                        />
                      ) : (
                        <HorizontalInputsArray
                          form={form}
                          processOptions={runSettingsData?.processOptions}
                          inputs={inputGroup}
                          style={style}
                          mainScriptHeaderConfig={
                            runPipelineScripts.mainScriptHeaderConfig
                          }
                          renderInputOptions={renderInputOptions}
                          isArray={isArray}
                          readOnly={readOnly}
                        />
                      )}
                    </div>
                  )}
                </div>
              )
            )}
          </CollapsibleContainer>
        )
      )}
      {openShowSettingsModal.length > 0 && (
        <ShowSettingsModal
          showSettings={openShowSettingsModal}
          setShowSettings={setOpenShowSettingsModal}
          form={form}
          allGlobalInputs={allGlobalInputs}
          allPipelineInputs={allPipelineInputs}
          allProcessAndModuleInputs={allProcessAndModuleInputs}
          processOptions={runSettingsData?.processOptions}
          runUUID={runSettingsData?.runUUID}
          mainScriptHeaderConfig={runPipelineScripts.mainScriptHeaderConfig}
          paramsInputs={paramsInputs}
          setParamsInputs={setParamsInputs}
          readOnly={readOnly}
          editorProcesses={editorProcesses}
          runOwnerId={runSettingsData?.owner?.id}
        />
      )}
    </>
  );
};

export default ViaPipelineInputs;
